// src/pages/ClientDetails.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { clientsApi, assessmentsApi, annualReviewApi } from '../services/api';
import ImportNistDialog from '../components/ImportNistDialog';
import TrendingReportDialog from '../components/TrendingReportDialog';
import TicketScheduleSection from '../components/TicketScheduleSection';
import AnnualSecurityReviewDialog from '../components/AnnualSecurityReviewDialog';

const ClientDetails = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [assessments, setAssessments] = useState([]);
  const [loadingAssessments, setLoadingAssessments] = useState(false);
  const [trendingDialogOpen, setTrendingDialogOpen] = useState(false);
  const [annualReviewOpen, setAnnualReviewOpen] = useState(false);
  const [savedReview, setSavedReview] = useState(null);
  const [reviewSummaryExpanded, setReviewSummaryExpanded] = useState(false);
  const [downloadingReviewPdf, setDownloadingReviewPdf] = useState(false);

  useEffect(() => {
    loadClient();
    loadAssessments();
    loadAnnualReview();
  }, [clientId]);

  const loadAnnualReview = async () => {
    try {
      const response = await annualReviewApi.getClientData(clientId);
      setSavedReview(response.data.saved_review || null);
    } catch (error) {
      // Not critical — silently ignore
    }
  };

  const handleDownloadReviewPdf = async () => {
    if (!savedReview) return;
    try {
      setDownloadingReviewPdf(true);
      const response = await annualReviewApi.generatePdf(
        clientId,
        savedReview.selected_sections || {},
        savedReview.ciso_summary || ''
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const safeName = (client?.name || 'client').replace(/\s+/g, '_').replace(/\//g, '_');
      const month = new Date().toISOString().slice(0, 7).replace('-', '');
      link.setAttribute('download', `${safeName}_Annual_Security_Review_${month}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to generate PDF');
    } finally {
      setDownloadingReviewPdf(false);
    }
  };

  const loadClient = async () => {
    try {
      const response = await clientsApi.getById(clientId);
      setClient(response.data);
    } catch (error) {
      console.error('Error loading client:', error);
      alert('Failed to load client');
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  };

  const loadAssessments = async () => {
    try {
      setLoadingAssessments(true);
      const response = await assessmentsApi.getAll({ client_id: clientId });
      setAssessments(response.data.assessments || []);
    } catch (error) {
      console.error('Error loading assessments:', error);
    } finally {
      setLoadingAssessments(false);
    }
  };

  const handleStartNewAssessment = () => {
    // Navigate to create new assessment page with client pre-selected
    navigate(`/assessments/new?client_id=${clientId}`);
  };

  const handleTrendingCompare = async (baselineId, currentId) => {
    // Navigate to trending report page with selected assessments
    navigate(`/trending-report?baseline=${baselineId}&current=${currentId}`);
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;

    try {
      setUploadingLogo(true);
      const formData = new FormData();
      formData.append('logo', logoFile);

      const token = localStorage.getItem('access_token');
      const response = await fetch(`/api/clients/${clientId}/logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        alert('Logo uploaded successfully');
        setLogoFile(null);
        setLogoPreview(null);
        loadClient(); // Reload to get updated logo
      } else {
        const data = await response.json();
        alert('Failed to upload logo: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Are you sure you want to delete this logo?')) return;

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`/api/clients/${clientId}/logo`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('Logo deleted successfully');
        loadClient();
      } else {
        const data = await response.json();
        alert('Failed to delete logo: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting logo:', error);
      alert('Failed to delete logo');
    }
  };

  const handleImport = async (file, completionDate) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('completion_date', completionDate);
      
      const response = await clientsApi.importNistAssessment(clientId, formData);
      
      const stats = response.data.stats;
      let message = `Assessment imported successfully!\n\n`;
      message += `• ${stats.matched} of ${stats.total_responses} responses matched\n`;
      
      if (stats.unmatched > 0) {
        message += `• ${stats.unmatched} responses could not be matched\n`;
        if (stats.unmatched_codes && stats.unmatched_codes.length > 0) {
          message += `• Unmatched codes: ${stats.unmatched_codes.join(', ')}...\n`;
        }
      }
      
      alert(message);
      
      // Navigate to the new assessment
      navigate(`/assessments/${response.data.assessment_id}`);
      
    } catch (error) {
      console.error('Error importing assessment:', error);
      throw error; // Re-throw so dialog can handle it
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!client) {
    return <div>Client not found</div>;
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <Link to="/clients" style={{ color: '#3498db', textDecoration: 'none', marginBottom: '10px', display: 'inline-block' }}>
          ← Back to Clients
        </Link>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ margin: 0 }}>{client.name}</h1>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => setAnnualReviewOpen(true)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6B3FA0',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              📋 Annual Security Review
            </button>
            <button
              onClick={() => setTrendingDialogOpen(true)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#9b59b6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              📊 View Trending Report
            </button>
            <button
              onClick={handleStartNewAssessment}
              style={{
                padding: '10px 20px',
                backgroundColor: '#e67e22',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              ➕ Start New Assessment
            </button>
            <button
              onClick={() => setImportDialogOpen(true)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#27ae60',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              📥 Import NIST Assessment
            </button>
            <button
              onClick={() => setShowEditModal(true)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Edit Client
            </button>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px' }}>
        {/* Client Information */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          padding: '20px'
        }}>
          <h2>Client Information</h2>
          
          <div style={{ marginTop: '20px' }}>
            <InfoRow label="Client Name" value={client.name} />
            <InfoRow label="Industry" value={client.industry || 'N/A'} />
            <InfoRow label="Contact Name" value={client.contact_name || 'N/A'} />
            <InfoRow label="Contact Email" value={client.contact_email || 'N/A'} />
            <InfoRow label="Primary Domain" value={client.domain || (client.contact_email ? client.contact_email.split('@')[1] : 'N/A')} />
            <InfoRow label="DW Alert Emails" value={client.dw_alert_emails || 'None configured'} />
            <InfoRow label="Do Not Copy Kyber" value={
              <span style={{
                padding: '3px 8px',
                borderRadius: '4px',
                backgroundColor: client.dw_no_copy_kyber ? '#fff3cd' : '#d4edda',
                color: client.dw_no_copy_kyber ? '#856404' : '#155724',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                {client.dw_no_copy_kyber ? 'Yes — Kyber suppressed' : 'No'}
              </span>
            } />
            <InfoRow label="Contact Phone" value={client.contact_phone || 'N/A'} />
            <InfoRow label="Address" value={client.address || 'N/A'} />
            <InfoRow label="Status" value={
              <span style={{
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: client.is_active ? '#d4edda' : '#f8d7da',
                color: client.is_active ? '#155724' : '#721c24',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                {client.is_active ? 'Active' : 'Inactive'}
              </span>
            } />
            <InfoRow label="Created" value={new Date(client.created_at).toLocaleDateString()} />
          </div>
        </div>

        {/* Client Logo */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          padding: '20px'
        }}>
          <h2>Client Logo</h2>
          
          {/* Current Logo Display */}
          {client.logo_filename && (
            <div style={{ marginTop: '20px', marginBottom: '20px' }}>
              <img 
                src={`/api/clients/${clientId}/logo/${client.logo_filename}`}
                alt="Client logo"
                style={{
                  maxWidth: '100%',
                  maxHeight: '200px',
                  display: 'block',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '10px',
                  backgroundColor: '#f8f9fa'
                }}
              />
              <button
                onClick={handleDeleteLogo}
                style={{
                  marginTop: '10px',
                  padding: '8px 16px',
                  backgroundColor: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Delete Logo
              </button>
            </div>
          )}

          {/* Upload New Logo */}
          <div style={{ marginTop: '20px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
              {client.logo_filename ? 'Replace Logo' : 'Upload Logo'}
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  setLogoFile(file);
                  // Show preview
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setLogoPreview(reader.result);
                  };
                  reader.readAsDataURL(file);
                }
              }}
              style={{
                display: 'block',
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                marginBottom: '10px'
              }}
            />
            
            {logoPreview && (
              <div style={{ marginBottom: '10px' }}>
                <img 
                  src={logoPreview} 
                  alt="Preview" 
                  style={{
                    maxWidth: '100%',
                    maxHeight: '150px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '10px',
                    backgroundColor: '#f8f9fa'
                  }}
                />
              </div>
            )}
            
            {logoFile && (
              <button
                onClick={handleLogoUpload}
                disabled={uploadingLogo}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: uploadingLogo ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                  opacity: uploadingLogo ? 0.6 : 1
                }}
              >
                {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
              </button>
            )}
            
            <div style={{ marginTop: '15px', fontSize: '12px', color: '#666' }}>
              <p style={{ margin: '5px 0' }}>Supported formats: PNG, JPG, JPEG, GIF</p>
              <p style={{ margin: '5px 0' }}>Recommended: 800x400 pixels or similar ratio</p>
            </div>
          </div>
        </div>
      </div>

      {/* Assessments List */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        padding: '30px',
        marginTop: '20px'
      }}>
        <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Assessments</h2>
        
        {loadingAssessments ? (
          <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
            Loading assessments...
          </div>
        ) : assessments.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
            <p style={{ marginBottom: '20px' }}>No assessments found for this client</p>
            <button
              onClick={handleStartNewAssessment}
              style={{
                padding: '12px 24px',
                backgroundColor: '#e67e22',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '16px'
              }}
            >
              ➕ Create First Assessment
            </button>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Assessment Name</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Type</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Created</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Progress</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {assessments.map(assessment => (
                  <tr key={assessment.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                    <td style={{ padding: '12px' }}>
                      <Link
                        to={`/assessments/${assessment.id}`}
                        style={{ color: '#3498db', textDecoration: 'none', fontWeight: 'bold' }}
                      >
                        {assessment.name}
                      </Link>
                    </td>
                    <td style={{ padding: '12px' }}>{assessment.assessment_type_name}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        backgroundColor: assessment.status === 'completed' ? '#27ae60' :
                                       assessment.status === 'in_progress' ? '#3498db' :
                                       assessment.status === 'reopened' ? '#f39c12' : '#95a5a6',
                        color: 'white'
                      }}>
                        {assessment.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {new Date(assessment.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {assessment.progress_percentage !== null ? 
                        `${Math.round(assessment.progress_percentage)}%` : 'N/A'}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <Link
                        to={`/assessments/${assessment.id}`}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#3498db',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          textDecoration: 'none',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          display: 'inline-block'
                        }}
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <EditClientModal
          client={client}
          onClose={() => setShowEditModal(false)}
          onSave={() => {
            setShowEditModal(false);
            loadClient();
          }}
        />
      )}

      {/* Annual Security Review Summary Card */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        padding: '24px',
        marginTop: '20px',
        borderLeft: '4px solid #6B3FA0'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: 0, color: '#6B3FA0' }}>📋 Annual Security Review</h2>
            {savedReview ? (
              <div style={{ marginTop: '4px', fontSize: '13px', color: '#666' }}>
                Last generated: {savedReview.updated_at
                  ? new Date(savedReview.updated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                  : 'Unknown'}
                {savedReview.ciso_summary_edited && (
                  <span style={{
                    marginLeft: '10px', padding: '2px 8px', borderRadius: '4px',
                    backgroundColor: '#fff3cd', color: '#856404', fontSize: '11px', fontWeight: 'bold'
                  }}>Manually Edited</span>
                )}
              </div>
            ) : (
              <div style={{ marginTop: '4px', fontSize: '13px', color: '#999' }}>No review generated yet</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            <button
              onClick={() => setAnnualReviewOpen(true)}
              style={{
                padding: '8px 16px', backgroundColor: '#6B3FA0', color: 'white',
                border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px'
              }}
            >
              {savedReview ? '✏️ Edit & Regenerate' : '🤖 Generate Review'}
            </button>
            {savedReview?.ciso_summary && (
              <button
                onClick={handleDownloadReviewPdf}
                disabled={downloadingReviewPdf}
                style={{
                  padding: '8px 16px', backgroundColor: '#27ae60', color: 'white',
                  border: 'none', borderRadius: '4px', cursor: downloadingReviewPdf ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold', fontSize: '13px', opacity: downloadingReviewPdf ? 0.6 : 1
                }}
              >
                {downloadingReviewPdf ? '⏳ Generating...' : '⬇ Download PDF'}
              </button>
            )}
          </div>
        </div>

        {savedReview?.ciso_summary && (
          <div style={{ marginTop: '16px' }}>
            <div style={{
              backgroundColor: '#f8f4ff', borderRadius: '6px', padding: '16px',
              fontSize: '13px', lineHeight: '1.6', color: '#333',
              whiteSpace: 'pre-wrap',
              maxHeight: reviewSummaryExpanded ? 'none' : '160px',
              overflow: 'hidden',
              position: 'relative'
            }}>
              {savedReview.ciso_summary}
              {!reviewSummaryExpanded && (
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0, height: '50px',
                  background: 'linear-gradient(transparent, #f8f4ff)'
                }} />
              )}
            </div>
            <button
              onClick={() => setReviewSummaryExpanded(!reviewSummaryExpanded)}
              style={{
                marginTop: '6px', background: 'none', border: 'none',
                color: '#6B3FA0', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', padding: 0
              }}
            >
              {reviewSummaryExpanded ? '▲ Show less' : '▼ Show full summary'}
            </button>
          </div>
        )}
      </div>

      {/* Ticket Schedules */}
      <TicketScheduleSection clientId={clientId} clientName={client.name} />

      {/* Import NIST Assessment Dialog */}
      <ImportNistDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        clientId={clientId}
        clientName={client.name}
        onImport={handleImport}
      />

      {/* Trending Report Dialog */}
      <TrendingReportDialog
        open={trendingDialogOpen}
        onClose={() => setTrendingDialogOpen(false)}
        clientId={clientId}
        clientName={client.name}
        onCompare={handleTrendingCompare}
      />

      <AnnualSecurityReviewDialog
        open={annualReviewOpen}
        onClose={() => { setAnnualReviewOpen(false); loadAnnualReview(); }}
        clientId={clientId}
        clientName={client.name}
      />
    </div>
  );
};

// Helper component for info rows
const InfoRow = ({ label, value }) => (
  <div style={{ 
    display: 'grid', 
    gridTemplateColumns: '150px 1fr', 
    padding: '12px 0', 
    borderBottom: '1px solid #eee' 
  }}>
    <div style={{ fontWeight: 'bold', color: '#666' }}>{label}:</div>
    <div>{value}</div>
  </div>
);

// Edit Client Modal Component
const EditClientModal = ({ client, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: client.name || '',
    industry: client.industry || '',
    contact_name: client.contact_name || '',
    contact_email: client.contact_email || '',
    domain: client.domain || '',
    contact_phone: client.contact_phone || '',
    address: client.address || '',
    dw_alert_emails: client.dw_alert_emails || '',
    dw_no_copy_kyber: !!client.dw_no_copy_kyber
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await clientsApi.update(client.id, formData);
      alert('Client updated successfully');
      onSave();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update client');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={modalOverlayStyle}>
      <div style={modalContentStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0 }}>Edit Client</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#999'
            }}
          >
            ×
          </button>
        </div>

        {error && (
          <div style={{
            padding: '10px',
            marginBottom: '20px',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '4px',
            color: '#c00'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Client Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              style={inputStyle}
            />
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Industry</label>
            <input
              type="text"
              name="industry"
              value={formData.industry}
              onChange={handleInputChange}
              style={inputStyle}
            />
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Contact Name</label>
            <input
              type="text"
              name="contact_name"
              value={formData.contact_name}
              onChange={handleInputChange}
              style={inputStyle}
            />
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Contact Email</label>
            <input
              type="email"
              name="contact_email"
              value={formData.contact_email}
              onChange={handleInputChange}
              style={inputStyle}
            />
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>
              Domain(s)&nbsp;
              <span style={{fontWeight:400,color:'#888',fontSize:'12px'}}>(comma-separated, for SPF/DMARC/DKIM checks)</span>
            </label>
            <input
              type="text"
              name="domain"
              value={formData.domain}
              onChange={handleInputChange}
              placeholder="e.g. kybersecure.com"
              style={inputStyle}
            />
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Contact Phone</label>
            <input
              type="tel"
              name="contact_phone"
              value={formData.contact_phone}
              onChange={handleInputChange}
              style={inputStyle}
            />
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Address</label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              rows="3"
              style={inputStyle}
            />
          </div>

          <div style={{ ...formGroupStyle, borderTop: '1px solid #eee', paddingTop: '16px', marginTop: '8px' }}>
            <label style={{ ...labelStyle, color: '#555' }}>Dark Web Alert Emails</label>
            <input
              type="text"
              name="dw_alert_emails"
              value={formData.dw_alert_emails}
              onChange={handleInputChange}
              placeholder="e.g. it@acme.com, cto@acme.com"
              style={inputStyle}
            />
            <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
              Comma-separated list of addresses to receive dark web exposure digest emails.
              Leave blank to use the default behavior (internal CW ticket only).
            </div>
          </div>

          <div style={{ ...formGroupStyle, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              type="checkbox"
              id="dw_no_copy_kyber"
              name="dw_no_copy_kyber"
              checked={formData.dw_no_copy_kyber}
              onChange={handleInputChange}
              style={{ width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
            />
            <label htmlFor="dw_no_copy_kyber" style={{ ...labelStyle, margin: 0, cursor: 'pointer', fontWeight: 400 }}>
              <strong>Do Not Copy Kyber</strong> — suppress all internal Kyber notifications and CW tickets for this client's dark web alerts
            </label>
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                padding: '10px 20px',
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.7 : 1
              }}
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000
};

const modalContentStyle = {
  backgroundColor: 'white',
  padding: '30px',
  borderRadius: '8px',
  width: '90%',
  maxWidth: '600px',
  maxHeight: '90vh',
  overflowY: 'auto'
};

const formGroupStyle = {
  marginBottom: '20px'
};

const labelStyle = {
  display: 'block',
  marginBottom: '5px',
  fontWeight: 'bold',
  fontSize: '14px'
};

const inputStyle = {
  width: '100%',
  padding: '10px',
  border: '1px solid #ddd',
  borderRadius: '4px',
  fontSize: '14px',
  boxSizing: 'border-box'
};

export default ClientDetails;
